import Button from "components-redesign/Button/Button";
import { makeStyles } from "components/mui-core";
import { AnimatePresence, motion, Transition } from "framer-motion";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import Snackbar from "./Snackbar";
import {
  HOVER_CLOSE_DELAY,
  MAX_COLLAPSED_SLIVERS,
  SLIVER_HEIGHT,
  SLIVER_INSET,
  SLIVER_OVERLAP,
  SNACKBAR_BORDER_RADIUS,
  SNACKBAR_COLORS,
  SNACKBAR_ELEVATION,
  SNACKBAR_WIDTH,
  STACK_GAP,
  STACK_INSET,
  STACK_Z_INDEX,
} from "./constants";
import { snackbar } from "./snackbarStore";
import { SnackbarItem } from "./types";
import { useSnackbarItems } from "./useSnackbar";
import { useSnackbarTimers } from "./useSnackbarTimers";

const TRANSITION: Transition = { duration: 0.2, ease: "easeOut" };
/** Material standard easing: leaves quickly, settles slowly. */
const REVEAL_TRANSITION: Transition = {
  duration: 0.2,
  ease: [0.2, 0, 0, 1],
};
/** Cards unfold one after another rather than all at once. */
const REVEAL_STAGGER = 0.02;

/**
 * Element identity follows the logical slot, not the item id. A keyed snackbar
 * that replaces another therefore swaps its content in place instead of playing
 * an exit and an enter animation.
 */
const renderKey = (item: SnackbarItem) =>
  item.key ? `key:${item.key}` : `id:${item.id}`;

/** Room for the cards' drop shadow inside the scroll container. */
const SHADOW_GUTTER = 6;

const useStyles = makeStyles()({
  root: {
    position: "fixed",
    top: STACK_INSET,
    right: STACK_INSET,
    zIndex: STACK_Z_INDEX,
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: STACK_GAP,
    width: SNACKBAR_WIDTH,
    maxWidth: `calc(100vw - ${STACK_INSET * 2}px)`,
    // Let clicks through the empty area around the cards.
    pointerEvents: "none",
  },
  rootOpen: {
    pointerEvents: "auto",
  },
  list: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    width: "100%",
    padding: SHADOW_GUTTER,
    margin: -SHADOW_GUTTER,
  },
  listExpanded: {
    gap: STACK_GAP,
    maxHeight: `calc(100vh - ${STACK_INSET * 2 + 56}px)`,
    overflowY: "auto",
    // Claimed only while open, else the empty column swallows page clicks.
    pointerEvents: "auto",
  },
  clearAll: {
    pointerEvents: "auto",
  },
  item: {
    width: "100%",
  },
  itemTop: {
    pointerEvents: "auto",
  },
  sliver: {
    pointerEvents: "auto",
    height: SLIVER_HEIGHT,
    borderRadius: SNACKBAR_BORDER_RADIUS,
    boxShadow: SNACKBAR_ELEVATION,
    flexShrink: 0,
  },
});

/**
 * The single host for every snackbar in the app, mounted once in App. More than
 * one collapses into a stack that expands on hover with a "Clear all" pill.
 */
function SnackbarStack() {
  const { classes, cx } = useStyles();
  const items = useSnackbarItems();
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const cancelClose = useCallback(() => {
    if (!closeTimerRef.current) return;
    clearTimeout(closeTimerRef.current);
    closeTimerRef.current = undefined;
  }, []);

  const openStack = useCallback(() => {
    cancelClose();
    setIsOpen(true);
  }, [cancelClose]);

  const closeStack = useCallback(() => {
    cancelClose();
    closeTimerRef.current = setTimeout(() => {
      closeTimerRef.current = undefined;
      if (rootRef.current?.matches(":hover")) return;
      setIsOpen(false);
    }, HOVER_CLOSE_DELAY);
  }, [cancelClose]);

  useEffect(() => cancelClose, [cancelClose]);

  // Newest first, matching the design.
  const ordered = useMemo(() => [...items].reverse(), [items]);

  useSnackbarTimers(items, isOpen);

  // Unmounting under the pointer means mouseleave never fires, which would
  // leave the next batch stuck open.
  useEffect(() => {
    if (!items.length) {
      cancelClose();
      setIsOpen(false);
    }
  }, [items.length, cancelClose]);

  const isExpanded = ordered.length > 1 && isOpen;
  const newest: SnackbarItem | undefined = ordered[0];
  const slivers = ordered.slice(1, 1 + MAX_COLLAPSED_SLIVERS);

  if (!newest) return null;

  const renderCard = (item: SnackbarItem) => (
    <Snackbar
      variant={item.variant}
      message={item.message}
      actions={item.actions}
      closable={item.closable}
      actionsPlacement={item.actionsPlacement}
      dataQa={item.dataQa}
      onClose={() => snackbar.dismiss(item.id)}
    />
  );

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== "Escape") return;
    event.stopPropagation();
    snackbar.dismiss(newest.id);
  };

  return (
    <div
      ref={rootRef}
      className={cx(classes.root, { [classes.rootOpen]: isOpen })}
      data-qa="snackbar-stack"
      aria-live="polite"
      onMouseEnter={openStack}
      onMouseLeave={closeStack}
      onFocus={openStack}
      onBlur={closeStack}
      onKeyDown={handleKeyDown}>
      {isExpanded && (
        <motion.div
          className={classes.clearAll}
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={TRANSITION}>
          <Button
            size="small"
            data-qa="snackbar-clear-all"
            onClick={() => snackbar.dismissAll()}>
            Clear all
          </Button>
        </motion.div>
      )}

      <div className={cx(classes.list, { [classes.listExpanded]: isExpanded })}>
        <AnimatePresence initial={false}>
          <motion.div
            key={renderKey(newest)}
            layout
            className={cx(classes.item, classes.itemTop)}
            initial={{ opacity: 0, x: 32 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 32 }}
            transition={TRANSITION}>
            {renderCard(newest)}
          </motion.div>
        </AnimatePresence>

        {isExpanded &&
          ordered.slice(1).map((item, index) => (
            <motion.div
              key={renderKey(item)}
              layout
              className={classes.item}
              initial={{ opacity: 0, y: -12, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{
                ...REVEAL_TRANSITION,
                delay: index * REVEAL_STAGGER,
              }}>
              {renderCard(item)}
            </motion.div>
          ))}

        {!isExpanded &&
          slivers.map((item, index) => (
            <div
              key={`sliver-${renderKey(item)}`}
              className={classes.sliver}
              data-qa="snackbar-sliver"
              style={{
                backgroundColor: SNACKBAR_COLORS[item.variant],
                width: `calc(100% - ${(index + 1) * SLIVER_INSET}px)`,
                marginTop: -SLIVER_OVERLAP,
                // Paints behind the card above, inside the stack's own context.
                zIndex: -(index + 1),
              }}
            />
          ))}
      </div>
    </div>
  );
}

export default SnackbarStack;
